/**
 * /superadmin/demos
 *
 * Generate per-prospect demo access codes. Each code maps to a row in
 * the `demo_sessions` table (migration 018) and grants role-picker
 * access at /demo/{code}.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  PlayCircle, Plus, Search, Copy, X, Check, Calendar, Users, Activity,
  TrendingUp, AlertTriangle,
} from "lucide-react";
import DemoResetButton from "../../components/demo/DemoResetButton";
import { toast } from "../../components/ui/Toast";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const NEON     = "#00FF99";

// ======================================================================
// THIS PICKER IS AN ANNOTATION, NOT A SWITCH. READ BEFORE RELYING ON IT.
// ======================================================================
//
// Ticking a box here does NOT enable a module for the demo tenant.
// The selection is stored on demo_sessions.modules (a JSONB column,
// demo/models.py:33), written once at creation (demo/routes.py:665), and
// read in exactly three places — all of which echo it straight back to
// this screen (routes.py:578, :789, :837). Nothing consults it when a
// demo user signs in, and it never writes an org_modules row. The list
// view's "N modules" is counting this array and nothing else.
//
// TO MAKE A MODULE ACTUALLY APPEAR IN A DEMO, enable it for the demo
// ORGANISATION on the superadmin Modules screen. That writes org_modules,
// which is what /api/modules/my answers from and what the sidebar reads.
//
// THE KEYS HERE ARE ALSO A DIFFERENT VOCABULARY. `tickets`, `kira` and
// `dms` are in no backend list; the backend's ALL_MODULES has core_hr,
// engagement, ai_chat, video_messaging, learning, expenses and benefits,
// none of which appear here. That is survivable only because the values
// decide nothing — the moment this picker is wired to entitlement, three
// of its eight keys will silently match no module.
//
// er_cases is added below so the annotation is complete and uses the
// backend's spelling. It is NOT a way to give a demo ER.
const ALL_MODULES = [
  { key: "leave",        label: "Leave Management" },
  { key: "attendance",   label: "Attendance"       },
  { key: "payroll",      label: "Payroll"          },
  { key: "performance",  label: "Performance"      },
  { key: "tickets",      label: "Tickets"          },
  { key: "projects",     label: "Projects"         },
  { key: "kira",         label: "KIRA AI Assistant" },
  { key: "dms",          label: "Direct Messages"  },
  { key: "er_cases",     label: "ER Cases"         },
];

const EXPIRY_PRESETS = [
  { value: 24,    label: "24 hours" },
  { value: 48,    label: "48 hours" },
  { value: 72,    label: "72 hours" },
  { value: 168,   label: "7 days"   },
  { value: 720,   label: "30 days"  },
];

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("token") || ""}` };
}

function appDomain() {
  return typeof window !== "undefined" ? window.location.origin : "";
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function statusFor(s) {
  if (!s.is_active)   return { tone: "gray",   label: "Deactivated" };
  if (s.is_expired)   return { tone: "red",    label: "Expired"     };
  if (s.use_count >= s.max_uses) return { tone: "red", label: "Limit reached" };
  return { tone: "green", label: "Active" };
}

const tone = (t) => {
  switch (t) {
    case "green": return { bg: "rgba(0,255,153,0.10)", color: NEON,        border: "rgba(0,255,153,0.4)" };
    case "red":   return { bg: "rgba(255,77,77,0.10)", color: "#ff8a8a",   border: "rgba(255,77,77,0.4)" };
    default:      return { bg: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.6)", border: "rgba(255,255,255,0.15)" };
  }
};

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

export default function SuperadminDemosPage() {
  const [sessions, setSessions]   = useState([]);
  const [loading,  setLoading]    = useState(true);
  const [err,      setErr]        = useState(null);

  const [search,   setSearch]     = useState("");
  const [filter,   setFilter]     = useState("all");   // all | active | expired

  const [showModal, setShowModal] = useState(false);
  const [createdResult, setCreatedResult] = useState(null);
  const [copyTick, setCopyTick]   = useState(null);

  const refresh = async () => {
    setLoading(true); setErr(null);
    try {
      const res  = await fetch(`${API_BASE}/api/demo/sessions`, { headers: authHeaders() });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      setSessions(Array.isArray(body?.sessions) ? body.sessions : []);
    } catch (e) {
      setErr(typeof e.message === "string" ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sessions.filter((s) => {
      if (filter === "active" && (!s.is_active || s.is_expired)) return false;
      if (filter === "expired" && !s.is_expired) return false;
      if (!q) return true;
      return [
        s.code, s.label, s.prospect_name, s.prospect_company, s.prospect_email,
      ].some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [sessions, search, filter]);

  const stats = useMemo(() => {
    const total      = sessions.length;
    const active     = sessions.filter((s) => s.is_active && !s.is_expired).length;
    const totalUses  = sessions.reduce((a, s) => a + (s.use_count || 0), 0);
    const avgUses    = total ? (totalUses / total).toFixed(1) : "0.0";
    return { total, active, totalUses, avgUses };
  }, [sessions]);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyTick(key);
      setTimeout(() => setCopyTick(null), 1200);
    } catch { /* ignore */ }
  };

  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this demo session? The code will stop working immediately.")) return;
    try {
      const res = await fetch(`${API_BASE}/api/demo/sessions/${id}/deactivate`, {
        method: "PATCH", headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await refresh();
      toast.success("Demo session deactivated");
    } catch (e) { toast.error(`Deactivate failed: ${e.message}`); }
  };

  const extend = async (id) => {
    const hours = parseInt(window.prompt("Extend by how many hours?", "24") || "0", 10);
    if (!hours || hours <= 0) return;
    try {
      const res = await fetch(`${API_BASE}/api/demo/sessions/${id}/extend`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ hours }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await refresh();
      toast.success(`Extended by ${hours} hour${hours === 1 ? "" : "s"}`);
    } catch (e) { toast.error(`Extend failed: ${e.message}`); }
  };

  const accessUrl = (code) => `${appDomain()}/demo/${code}`;

  return (
    <div className="p-6 text-white space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-[#00FF99] flex items-center gap-2">
            <PlayCircle size={22} /> Demo Sessions
          </h1>
          <p className="text-white/40 text-sm mt-0.5">
            Generate access codes for prospects to explore KweryAI.
          </p>
        </div>
        <DemoResetButton variant="button" />
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={() => { setCreatedResult(null); setShowModal(true); }}
          className="flex items-center gap-2 text-xs font-medium px-4 py-2 rounded-lg bg-[#00FF99]/15 text-[#00FF99] border border-[#00FF99]/40 hover:bg-[#00FF99]/25 transition"
        >
          <Plus size={14} /> Create Demo Session
        </button>

        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            type="text" placeholder="Search code, label, prospect…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-black/40 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-[#00FF99]/40"
          />
        </div>

        <div className="flex items-center gap-1 bg-black/40 border border-white/10 rounded-lg p-1">
          {["all", "active", "expired"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`text-xs px-3 py-1 rounded-md transition capitalize ${
                filter === f ? "bg-[#00FF99]/15 text-[#00FF99]" : "text-white/40 hover:text-white"
              }`}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard Icon={Calendar}     label="Total sessions" value={stats.total} />
        <StatCard Icon={Activity}     label="Active"         value={stats.active}     accent />
        <StatCard Icon={Users}        label="Total uses"     value={stats.totalUses} />
        <StatCard Icon={TrendingUp}   label="Avg uses / session" value={stats.avgUses} />
      </div>

      {/* Sessions table */}
      <div className={`${card} overflow-x-auto`}>
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-widest text-[#00FF99]/60">
            <tr className="border-b border-white/5">
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Label</th>
              <th className="px-4 py-3">Prospect</th>
              <th className="px-4 py-3">Company</th>
              <th className="px-4 py-3">Modules</th>
              <th className="px-4 py-3">Uses</th>
              <th className="px-4 py-3">Expires</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="px-4 py-6 text-center text-white/30">Loading…</td></tr>
            )}
            {!loading && err && (
              <tr><td colSpan={9} className="px-4 py-6 text-center text-red-300">
                <AlertTriangle size={14} className="inline mr-1" />{err}
              </td></tr>
            )}
            {!loading && !err && filtered.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-6 text-center text-white/30">
                No demo sessions yet — click "Create Demo Session" to issue your first code.
              </td></tr>
            )}
            {!loading && !err && filtered.map((s) => {
              const st = statusFor(s);
              const t  = tone(st.tone);
              return (
                <tr key={s.id} className="border-b border-white/5 hover:bg-white/[0.02] transition">
                  <td className="px-4 py-3">
                    <button onClick={() => copy(s.code, `code-${s.id}`)}
                      title="Copy code"
                      className="inline-flex items-center gap-1.5 font-mono text-xs px-2 py-1 rounded-md border border-[#00FF99]/30 bg-[#00FF99]/5 text-[#00FF99] hover:bg-[#00FF99]/15">
                      {s.code}
                      {copyTick === `code-${s.id}` ? <Check size={11} /> : <Copy size={11} />}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-white/80">{s.label}</td>
                  <td className="px-4 py-3 text-white/60">{s.prospect_name || "—"}</td>
                  <td className="px-4 py-3 text-white/60">{s.prospect_company || "—"}</td>
                  <td className="px-4 py-3 text-white/50">
                    <span title={(s.modules || []).join(", ")}>
                      {(s.modules || []).length} modules
                    </span>
                  </td>
                  <td className="px-4 py-3 text-white/60">
                    {s.use_count} / {s.max_uses}
                  </td>
                  <td className="px-4 py-3 text-white/50">{fmtDate(s.expires_at)}</td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] font-medium uppercase tracking-widest px-2 py-1 rounded-full border"
                      style={{ background: t.bg, color: t.color, borderColor: t.border }}>
                      {st.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => copy(accessUrl(s.code), `url-${s.id}`)}
                        className="text-[11px] px-2 py-1 rounded-md border border-white/10 bg-white/5 text-white/60 hover:text-white hover:border-[#00FF99]/40">
                        {copyTick === `url-${s.id}` ? "Copied!" : "Copy link"}
                      </button>
                      <button onClick={() => extend(s.id)}
                        className="text-[11px] px-2 py-1 rounded-md border border-white/10 bg-white/5 text-white/60 hover:text-white hover:border-[#00FF99]/40">
                        Extend
                      </button>
                      <button onClick={() => deactivate(s.id)}
                        disabled={!s.is_active}
                        className="text-[11px] px-2 py-1 rounded-md border border-white/10 bg-white/5 text-white/60 hover:text-red-300 hover:border-red-400/40 disabled:opacity-30 disabled:cursor-not-allowed">
                        Deactivate
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      {showModal && (
        <CreateModal
          onClose={() => { setShowModal(false); setCreatedResult(null); refresh(); }}
          created={createdResult}
          setCreated={setCreatedResult}
        />
      )}
    </div>
  );
}

function StatCard({ Icon, label, value, accent }) {
  return (
    <div className={`${card} px-5 py-4 flex items-center gap-4`}>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center"
        style={{
          background: accent ? "rgba(0,255,153,0.1)" : "rgba(255,255,255,0.05)",
          color:      accent ? NEON : "rgba(255,255,255,0.5)",
        }}>
        <Icon size={18} />
      </div>
      <div>
        <div className="text-2xl font-bold text-white">{value}</div>
        <div className="text-[11px] uppercase tracking-widest text-white/40">{label}</div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Create-session modal
// ──────────────────────────────────────────────────────────────────────
function CreateModal({ onClose, created, setCreated }) {
  const [form, setForm] = useState({
    label:            "",
    prospect_name:    "",
    prospect_company: "",
    prospect_email:   "",
    modules:          ALL_MODULES.map((m) => m.key),
    expires_hours:    72,
    max_uses:         10,
    notes:            "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState(null);
  const [copyTick,   setCopyTick]   = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleModule = (key) =>
    set("modules",
      form.modules.includes(key)
        ? form.modules.filter((k) => k !== key)
        : [...form.modules, key]
    );

  const submit = async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (!form.label.trim()) { setError("Label is required."); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/demo/sessions`, {
        method:  "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body:    JSON.stringify(form),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      setCreated(body);
    } catch (e) {
      setError(typeof e.message === "string" ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyTick(key);
      setTimeout(() => setCopyTick(null), 1200);
    } catch { /* ignore */ }
  };

  const accessUrl = created ? `${appDomain()}${created.access_url || `/demo/${created.code}`}` : "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className={`${card} w-full max-w-2xl max-h-[90vh] overflow-y-auto`}>
        <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[#00FF99] uppercase tracking-widest">
            {created ? "Demo session created" : "Create demo session"}
          </h2>
          <button onClick={onClose} className="text-white/40 hover:text-white">
            <X size={16} />
          </button>
        </div>

        {!created ? (
          <form onSubmit={submit} className="p-6 space-y-4">
            <Row label="Label *" hint="Internal name shown only to your team (e.g. 'Acme Corp Demo — May 2026').">
              <input value={form.label} onChange={(e) => set("label", e.target.value)}
                placeholder="Acme Corp Demo — May 2026"
                className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-[#00FF99]/50" />
            </Row>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Row label="Prospect name">
                <input value={form.prospect_name} onChange={(e) => set("prospect_name", e.target.value)}
                  placeholder="John Smith"
                  className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-[#00FF99]/50" />
              </Row>
              <Row label="Prospect company">
                <input value={form.prospect_company} onChange={(e) => set("prospect_company", e.target.value)}
                  placeholder="Acme Corp"
                  className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-[#00FF99]/50" />
              </Row>
            </div>
            <Row label="Prospect email">
              <input type="email" value={form.prospect_email}
                onChange={(e) => set("prospect_email", e.target.value)}
                placeholder="john@acme.com"
                className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-[#00FF99]/50" />
            </Row>

            <Row label="Modules" hint="Which features the prospect can see.">
              <div className="grid grid-cols-2 gap-2 mt-1">
                {ALL_MODULES.map((m) => (
                  <label key={m.key} className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                    <input type="checkbox"
                      checked={form.modules.includes(m.key)}
                      onChange={() => toggleModule(m.key)}
                      className="w-3.5 h-3.5 accent-[#00FF99]" />
                    {m.label}
                  </label>
                ))}
              </div>
            </Row>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Row label="Expires in">
                <select value={form.expires_hours}
                  onChange={(e) => set("expires_hours", Number(e.target.value))}
                  className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00FF99]/50">
                  {EXPIRY_PRESETS.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </Row>
              <Row label="Max uses" hint="How many times this code can be used before it auto-disables.">
                <input type="number" min={1} max={1000} value={form.max_uses}
                  onChange={(e) => set("max_uses", Math.max(1, Number(e.target.value) || 1))}
                  className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00FF99]/50" />
              </Row>
            </div>

            <Row label="Notes (internal)">
              <textarea value={form.notes} rows={2}
                onChange={(e) => set("notes", e.target.value)}
                placeholder="Anything to remember about this prospect or deal."
                className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 outline-none focus:border-[#00FF99]/50" />
            </Row>

            {error && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-200 text-xs px-3 py-2">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose}
                className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/50 hover:text-white">
                Cancel
              </button>
              <button type="submit" disabled={submitting}
                className="text-xs font-medium px-5 py-2 rounded-lg bg-[#00FF99]/15 text-[#00FF99] border border-[#00FF99]/40 hover:bg-[#00FF99]/25 disabled:opacity-40">
                {submitting ? "Creating…" : "Create session"}
              </button>
            </div>
          </form>
        ) : (
          <div className="p-6 space-y-4">
            <p className="text-sm text-white/70">
              Share this link with your prospect:
            </p>
            <div className="flex items-stretch gap-2">
              <code className="flex-1 font-mono text-sm bg-black/60 border border-[#00FF99]/30 rounded-lg px-3 py-2 text-[#00FF99] break-all select-all">
                {accessUrl}
              </code>
              <button onClick={() => copy(accessUrl, "url")}
                className="text-xs px-3 py-2 rounded-lg border border-[#00FF99]/30 bg-[#00FF99]/10 text-[#00FF99] hover:bg-[#00FF99]/20">
                {copyTick === "url" ? "Copied" : "Copy Link"}
              </button>
              <button onClick={() => copy(created.code, "code")}
                className="text-xs px-3 py-2 rounded-lg border border-white/10 bg-white/5 text-white/70 hover:text-white">
                {copyTick === "code" ? "Copied" : "Copy Code"}
              </button>
            </div>
            <div className="text-xs text-white/40">
              Code: <span className="font-mono text-[#00FF99]">{created.code}</span>
              {created.expires_at && (
                <> · Expires: {fmtDate(created.expires_at)}</>
              )}
              {typeof created.max_uses === "number" && (
                <> · Max uses: {created.max_uses}</>
              )}
            </div>
            <div className="flex justify-end pt-2">
              <button onClick={onClose}
                className="text-xs font-medium px-5 py-2 rounded-lg bg-[#00FF99]/15 text-[#00FF99] border border-[#00FF99]/40 hover:bg-[#00FF99]/25">
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <div>
      <label className="text-[10px] text-[#00FF99]/60 uppercase tracking-widest block mb-1.5">
        {label}
      </label>
      {children}
      {hint && <p className="text-[11px] text-white/30 mt-1">{hint}</p>}
    </div>
  );
}
